import { fs } from '@/theme/tokens';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Box, Button, Chip, IconButton, Popover,
  Stack, Tooltip, Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useAuthStore, useEmployeeOnly } from '@/store/auth';
import {
  useMarkAllNotificationsRead, useMarkNotificationRead,
  useNotifications, type Notification,
} from '@/api/notifications';
import { APP_BASE } from '@/lib/api-base';
// Keeps navigation inside the app (the old safeInAppPath guard, CVE-2026-53669)
// and adds the URL prefix the router's absolute routes need.
import { toAppPath } from '@/lib/app-link';

/** Compact relative time: "just now", "5m ago", "2h ago", "3d ago", else date. */
function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Topbar bell icon. Polls /api/notifications every 30s for the signed-in
 * user (their user_id AND role — backend returns the union). Badge shows
 * the unread count; popover lists rows with one-click "open + mark read".
 *
 * Both employees and HR see this — the recipient field on each row
 * decides who sees what. HR sees fan-out role-targeted rows
 * (DECLARATION_SUBMITTED for the whole HR role); employees see direct
 * user-id-targeted rows (DECLARATION_APPROVED for their login).
 */
export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? null;
  const role   = user?.role ?? null;
  const employeeOnly = useEmployeeOnly();

  // Poll the API in the background regardless of popover state — we want
  // the badge to update even while the user is in another tab. Drop to a
  // slower 60s pulse when the popover is closed; 5s while it's open so
  // dismissals feel instant.
  const q = useNotifications(
    { user_id: userId, role: role, status: 'UNREAD' },
    anchor ? 5_000 : 30_000,
  );
  const markRead    = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const items = q.data?.items ?? [];
  const unread = q.data?.unread_count ?? 0;

  const onRowClick = (n: Notification) => {
    setAnchor(null);
    if (n.status === 'PENDING') markRead.mutate(n.id);
    // Producers store `/app/x`, `/x` or the prefixed path; the router has no
    // basename, so it needs the full prefixed path. See lib/app-link.
    if (n.link_path) navigate(toAppPath(n.link_path));
  };

  if (!userId && !role) return null;

  return (
    <>
      <Tooltip title={unread ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} sx={{ ml: 0.5 }}>
          <Badge badgeContent={unread} color="error" max={99}
            slotProps={{ badge: { sx: { fontSize: fs.micro, height: 16, minWidth: 16 } } }}>
            <NotificationsIcon fontSize="small" sx={{ color: unread ? 'primary.main' : 'text.secondary' }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: {
          width: 390, maxHeight: 520, borderRadius: 3, overflow: 'hidden', mt: 1,
          boxShadow: '0 12px 32px rgba(24,40,72,0.16)',
        } } }}
      >
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
            Notifications
          </Typography>
          {unread > 0 && (
            <Chip size="small" color="primary" label={`${unread} new`}
              sx={{ height: 20, fontSize: fs.micro, fontWeight: 700 }} />
          )}
          {unread > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />}
              onClick={() => markAllRead.mutate({ user_id: userId, role })}
              disabled={markAllRead.isPending}>
              Mark all read
            </Button>
          )}
        </Stack>

        {items.length === 0 && (
          <Box sx={{ px: 3, py: 5, textAlign: 'center', color: 'text.secondary' }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
              display: 'grid', placeItems: 'center',
              bgcolor: (t) => `${t.palette.primary.main}12`, color: 'primary.main',
            }}>
              <NotificationsNoneRoundedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              You're all caught up
            </Typography>
            <Typography variant="caption">No new notifications right now.</Typography>
          </Box>
        )}

        {items.map((n) => {
          const pending = n.status === 'PENDING';
          return (
          <Box key={n.id}
            onClick={() => onRowClick(n)}
            sx={{
              px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider',
              cursor: n.link_path ? 'pointer' : 'default',
              boxShadow: pending ? (t) => `inset 3px 0 0 0 ${t.palette.primary.main}` : 'none',
              bgcolor: pending ? (t) => `${t.palette.primary.main}0a` : 'background.paper',
              transition: 'background 120ms',
              '&:hover': n.link_path ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{
                fontWeight: pending ? 700 : 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {n.subject}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.25,
              }}>
                {n.body}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip size="small" variant="outlined" label={n.kind.replace(/_/g, ' ').toLowerCase()}
                  sx={{ height: 18, fontSize: fs.micro, textTransform: 'capitalize',
                    '& .MuiChip-label': { px: 0.75 } }} />
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: fs.micro }}>
                  {timeAgo(n.created_at)}
                </Typography>
              </Stack>
            </Box>
          </Box>
          );
        })}
        <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button size="small" fullWidth variant="text"
            onClick={() => {
              setAnchor(null);
              // Employees are confined to /me (AppShell), so their history lives there.
              navigate(`${APP_BASE}${employeeOnly ? '/me' : ''}/notifications`);
            }}>
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
}
